/**
 * Design-QA route for the /subscription screen (mirrors AwardsReview).
 * Mounts <SubscriptionView> with fabricated data — no Supabase session,
 * no Paddle. Pick the state via query params:
 *
 *   /subscription-review?state=none|trialing|active|cancelling|past_due|expired
 *   &unknown=1     — show the amber change-in-progress banner
 *   &confirming=1  — show the verifying veil over the status card
 *   &frame=1       — show the inline payment frame shell (loading overlay)
 *   &loading=1     — first-load skeleton
 *   &failed=1      — status load failure card
 */
import { useSearchParams } from 'react-router';
import { SubscriptionView } from './screens/Subscription.js';
import { PaymentFrame } from './subscription/PaymentFrame.js';
import type { DerivedStatus } from './subscription/access.js';
import type {
  CanonicalRow,
  PaymentMethodSummary,
  TransactionSummary,
} from './subscription/manage.js';

const ROW: CanonicalRow = {
  status: 'active',
  trial_ends_at: '2026-07-23T10:00:00Z',
  current_period_starts_at: '2026-07-01T10:00:00Z',
  current_period_ends_at: '2026-08-16T10:00:00Z',
  cancelled_at: null,
  provider_price_id: 'pri_mock',
};

const PM: PaymentMethodSummary = {
  brand: 'visa',
  last4: '4242',
  expiry_month: 9,
  expiry_year: 2028,
};

const TXNS: TransactionSummary[] = [
  { id: 't3', billed_at: '2026-07-16T10:00:00Z', status: 'completed', total: '500', currency: 'USD' },
  { id: 't2', billed_at: '2026-06-16T10:00:00Z', status: 'past_due', total: '500', currency: 'USD' },
  { id: 't1', billed_at: '2026-05-16T10:00:00Z', status: 'completed', total: '500', currency: 'USD' },
];

const noop = () => undefined;

export function SubscriptionReview() {
  const [params] = useSearchParams();
  const status = (params.get('state') ?? 'active') as DerivedStatus;
  const row: CanonicalRow | null =
    status === 'none'
      ? null
      : {
          ...ROW,
          status: status === 'cancelling' ? 'active' : status === 'expired' ? 'canceled' : status,
          cancelled_at: status === 'cancelling' ? '2026-08-16T10:00:00Z' : null,
        };

  return (
    <SubscriptionView
      status={status}
      row={row}
      paymentMethod={status === 'none' || status === 'expired' ? null : PM}
      transactions={status === 'none' || status === 'trialing' ? [] : TXNS}
      scheduledRedemption={
        status === 'active' || status === 'trialing'
          ? { coins: 278, usd_cents: 103, period_ends_at: ROW.current_period_ends_at! }
          : null
      }
      firstLoad={params.get('loading') === '1'}
      loadFailed={params.get('failed') === '1'}
      onRetryLoad={noop}
      confirming={params.get('confirming') === '1'}
      unknown={params.get('unknown') === '1'}
      onRecheck={noop}
      frame={
        params.get('frame') === '1' ? (
          <PaymentFrame
            mode={status === 'none' ? 'subscribe' : 'update'}
            email="qa@fluent.ge"
            userId="00000000-0000-0000-0000-000000000000"
            trialOrPeriodEnd={ROW.current_period_ends_at}
            onClose={noop}
            onCompleted={noop}
          />
        ) : undefined
      }
      frameOpen={params.get('frame') === '1'}
      onBack={noop}
      onSubscribe={noop}
      onResubscribe={noop}
      onFixCard={noop}
      onChangeCard={noop}
      onCancel={noop}
      onResume={noop}
    />
  );
}
